import { useCallback, useEffect, useRef, useState } from 'react';

import { useReanalyzeSingleImage } from '@/api/tag-suggestions';
import type { TagAnalysisStatus } from '@/types/api';

/**
 * The longest the single-image "Analyzing photo" overlay waits for its job: one run's 60 s
 * router deadline plus a retry.
 */
export const SINGLE_IMAGE_ANALYSIS_MAX_WAIT_MS = 3 * 60 * 1000;

interface SingleImageReanalysis {
  /** The image being re-analyzed, from the click until its analysis job has finished. */
  reanalyzingImageIndex: number | undefined;
  start: (imageIndex: number) => void;
}

/**
 * Re-analyze one photo and report it as in progress until the analysis JOB finishes, not
 * just while the enqueue request runs (which returns in ~100 ms while the AI Router call
 * takes seconds). "Finished" is the polled tag status leaving 'analyzing' after this run was
 * seen in it; a status read before that belongs to the previous run and is ignored.
 *
 * @param tagStatus the polled `useTagSuggestions(...).data?.status`
 * @param onStartFailed called when the enqueue request itself fails
 */
export function useSingleImageReanalysis(
  locationId: string | undefined,
  tagStatus: TagAnalysisStatus | undefined,
  onStartFailed: (error: unknown) => void
): SingleImageReanalysis {
  const mutation = useReanalyzeSingleImage();
  const [reanalyzingImageIndex, setReanalyzingImageIndex] = useState<number | undefined>(undefined);
  const sawAnalyzing = useRef(false);

  const start = useCallback(
    (imageIndex: number) => {
      if (!locationId) return;
      sawAnalyzing.current = false;
      setReanalyzingImageIndex(imageIndex);
      mutation.mutate(
        { locationId, imageIndex },
        {
          onSuccess: (response) => {
            // Nothing was queued: nothing to wait for.
            if (response.status !== 'analyzing') {
              setReanalyzingImageIndex(undefined);
            }
          },
          onError: (error) => {
            setReanalyzingImageIndex(undefined);
            onStartFailed(error);
          },
        }
      );
    },
    [locationId, mutation, onStartFailed]
  );

  // End when the job this click started has finished.
  const isPending = mutation.isPending;
  useEffect(() => {
    if (reanalyzingImageIndex === undefined || isPending) return;
    if (tagStatus === 'analyzing') {
      sawAnalyzing.current = true;
      return;
    }
    if (sawAnalyzing.current) {
      sawAnalyzing.current = false;
      setReanalyzingImageIndex(undefined);
    }
  }, [tagStatus, reanalyzingImageIndex, isPending]);

  // Backstop: a job that finished between two polls never shows as 'analyzing', and the
  // effect above would then wait forever.
  useEffect(() => {
    if (reanalyzingImageIndex === undefined) return undefined;
    const timer = setTimeout(() => {
      setReanalyzingImageIndex(undefined);
    }, SINGLE_IMAGE_ANALYSIS_MAX_WAIT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [reanalyzingImageIndex]);

  return { reanalyzingImageIndex, start };
}
